import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { categoryStructure } from '../data/catalog';
import {PriorityLinks} from '../components/PriorityFamilyContent';
import {UniversGuide} from '../components/FamilyGuide';
import { ArrowRight, ArrowLeft, BookOpen, Facebook, Tent, Wrench, Leaf, Sparkles } from 'lucide-react';
import { slugify } from '../utils/slugs';
import { useSeo } from '../seo/SeoProvider';

// Mapping des slugs vers les noms de catégories
const slugToCategory = {
  'evenementiel-barnum': 'Evenementiel - Barnum',
  'bricolage-travaux': 'Bricolage - Travaux',
  'jardinage-motoculture': 'Jardinage - Motoculture',
  'nettoyage-logement': 'Nettoyage - Logement'
};

// Mapping des catégories vers le type de catalogue (3dloc ou barnum)
const categoryToCatalogType = {
  'Evenementiel - Barnum': 'barnum',
  'Bricolage - Travaux': '3dloc',
  'Jardinage - Motoculture': '3dloc',
  'Nettoyage - Logement': '3dloc'
};

// Informations sur les univers (sans les URLs statiques)
const universInfo = {
  'Evenementiel - Barnum': {
    title: 'Évènementiel & Loisirs',
    description: 'Équipez-vous pour tous vos événements : mariages, anniversaires, cérémonies, séminaires. 3DLoc vous propose une large gamme de chapiteaux, mobilier, sono et structures gonflables.',
    color: '#22C55E',
    icon: Tent
  },
  'Bricolage - Travaux': {
    title: 'Bricolage & Travaux',
    description: "Équipez-vous pour tous vos projets de rénovation ou de construction. 3DLoc vous propose une large gamme d'outils électroportatifs, matériels de chantier et équipements pour le bricolage.",
    color: '#16A34A',
    icon: Wrench
  },
  'Jardinage - Motoculture': {
    title: 'Jardinage & Motoculture',
    description: "De la préparation de votre terrain à l'entretien de vos plantations, nous mettons à votre disposition toutes les machines nécessaires au bien-être de vos espaces verts.",
    color: '#15803D',
    icon: Leaf
  },
  'Nettoyage - Logement': {
    title: 'Nettoyage & Logement',
    description: "Un diagnostic thermique à réaliser, un nettoyage efficace de vos sols et façades ou encore une recherche de coupure de câble électrique ? Nous avons l'équipement qu'il vous faut.",
    color: '#166534',
    icon: Sparkles
  }
};

// Images des sous-catégories extraites du fichier Excel
const subcategoryImages = {
  "Location Barnums & Tentes": "/media/1752217005984-productimage-67652562dfe18b126c4da-7e0ec45b.jpg",
  "Location Plancher de réception": "/media/1739105371189-productimage-67652562dfe18b126c4da-74b9462a.jpg",
  "Location Tente pliantes": "/media/t83r742i-tente-20pliante-203x6m-540a625d.png",
  "Location chaises pliantes": "/media/1739534612954-productimage-67652562dfe18b126c4da-359f5666.jpg",
  "Location Tables pliantes": "/media/1739108456190-productimage-67652562dfe18b126c4da-bbdb4006.jpg",
  "Location Vaisselle": "/media/b5egb8fm-vaisselle-20pack-20eco-c85fad00.png",
  "Location chauffage": "/media/1739525523917-productimage-67652562dfe18b126c4da-40047844.jpg",
  "Location Son et Lumière": "/media/1739524491884-productimage-67652562dfe18b126c4da-c8ae4faa.jpg",
  "Location structure Gonflable": "/media/1747950868911-productimage-67652562dfe18b126c4da-b93a82a1.jpg",
  "Location Matériel Kermesse enfants": "/media/1740691628828-productimage-67652562dfe18b126c4da-9656a1b4.jpg",
  "Location Tireuse à bières ": "/media/us8wfqme-tireuse-202-20becs-6101fff5.png",
  "Matériel Restauration": "/media/1g2w9ve5-fontaine-20chocolat-40e52fec.png",
  "Location décoration": "/media/eds0bzlt-potelet-20dor-e2-80-9a-dc0e2c71.png",
  "Location Jeux ": "/media/svx45fmh-table-20roulette-a2611bf9.png",
  "Pack Barnum": "/media/1740517079914-productimage-67652562dfe18b126c4da-2a83f5b1.jpg",
  "Location Minipelle": "/media/image-976c978a.jpg",
  "Location Bétonnière": "/media/betonniere-340-litres-electrique-professionnel-p-b7ab47e9.jpg",
  "Location Plaque vibrante": "/media/plaque-vibrante-001-high-scwx8z-e5492b48.jpg",
  "Location tarière": "/media/tariere-00-high-745ce47e.jpg",
  "Location Marteau piqueur, burineur": "/media/brise-beton-03-high-tfw82s-8f404ca1.png",
  "Location Groupe électrogène": "/media/0002-high-b104a01c.jpg",
  "Location échafaudage": "/media/image-0e6288d8.jpg",
  "Location Scie tous matériaux": "/media/scie-onglets-sur-table-01-high-jlbtmk-73a3869b.jpg",
  "Location ponceuse": "/media/image-9f0499d0.jpg",
  "Location décolleuse": "/media/d-coleuse-papier-peint-location-loire-600x600-hi-4204427f.jpg",
  "Location lève-plaque": "/media/leve-plaque-001-high-1h7o6o-848f9316.jpg",
  "Location coupe carreaux carrelage": "/media/capture-d-cran-2025-02-06-133516-high-ob2437-271b5a26.jpg",
  "Location compresseur": "/media/pexels-photo-31095182-c46292f4.jpg",
  "Location pistolet à peinture": "https://images.unsplash.com/photo-1612430146325-87a163519863?w=600&auto=format",
  "Location déshumidifcateur": "/media/deshumidif-001-high-45703c82.jpg",
  "Location caméra thermique": "/media/cam-fir-01-high-b6c6e8f7.jpg",
  "Location détecteurs": "/media/capture-d-cran-2025-02-06-160551-high-18db62dc.jpg",
  "Location aspirateur": "/media/vacuum-cleaner-carpet-cleaner-housework-housekee-ca4471f8.jpg",
  "Location outillages": "/media/pince-a-sertir-per-multi-high-p88ywi-d4e5d61d.jpg",
  "Location Broyeur de végétaux": "/media/geotech-pro-pcs-155-bse-broyeur-de-branches-ther-44b6a91e.jpg",
  "Location fendeuse de buches": "/media/fendeuse-de-buche-22t-high-a03ebbe4.jpg",
  "Location débroussailleuse": "/media/image-60d8e3d3.webp",
  "Location Motoculteur & motobineuse": "/media/motoculteur-goldoni-uno-5-8-high-658ff44e.jpg",
  "Location scarificateur": "/media/marina-systems-s500b-arateur-professionnel-lames-13cb8314.jpg",
  "Location rouleau à gazon et semoir": "/media/51o1cwhxn8l-sl1000-high-bc264568.jpg",
  "Location souffleur": "/media/1738864799104-productimage-67652562dfe18b126c4da-78029195.jpg",
  "Location taille-haie": "/media/capture-d-cran-2025-02-06-184138-high-d3824474.jpg",
  "Location tronçonneuse & élagueuse": "/media/capture-d-cran-2025-02-06-184008-high-a6084322.jpg",
  "Location Shampouineuse": "/media/pexels-photo-4107284-7f6fd5b3.jpg",
  "Location Nettoyeur ": "/media/pexels-photo-6873132-9dfd48a3.jpg"
};

// Descriptions des sous-catégories
const subcategoryDescriptions = {
  "Location Barnums & Tentes": "Chapiteaux professionnels de 3x6m à 8x16m pour tous vos événements. Installation et livraison possibles.",
  "Location Plancher de réception": "Dalles et lames de terrasse pour créer un sol stable et élégant sous votre barnum.",
  "Location Tente pliantes": "Tentes pliantes pratiques de 3x3m à 3x6m, montage rapide et transport facile.",
  "Location chaises pliantes": "Chaises pliantes confortables : modèles ECO, cérémonie ou CONFORT. Bancs et tabourets également disponibles.",
  "Location Tables pliantes": "Tables rectangulaires, rondes et mange-debouts pour tous vos besoins de réception.",
  "Location Vaisselle": "Sets de vaisselle complets : assiettes, verres, tasses. Plusieurs styles disponibles.",
  "Location chauffage": "Chauffages électriques et à gaz pour maintenir vos invités au chaud en toute saison.",
  "Location Son et Lumière": "Sonorisation et éclairage professionnel : enceintes, micros, guirlandes et jeux de lumières.",
  "Location structure Gonflable": "Châteaux gonflables thématiques pour le bonheur des enfants : dinosaure, pirate, licorne...",
  "Location Matériel Kermesse enfants": "Machines à barbe à papa et pop-corn pour animer vos fêtes et kermesses.",
  "Location Tireuse à bières ": "Tireuses à bières professionnelles et fûts pour vos événements festifs.",
  "Matériel Restauration": "Équipement de restauration : fontaines à chocolat, machines à glaçons, percolateurs et remorques frigorifiques.",
  "Location décoration": "Accessoires décoratifs : potelets dorés, tapis rouge, chandeliers et arches pour sublimer votre événement.",
  "Location Jeux ": "Tables de jeux casino : roulette, poker, black-jack pour des soirées originales.",
  "Pack Barnum": "Packs complets barnum + mobilier pour 20 à 120 personnes. Tout inclus, clé en main !",
  "Location Minipelle": "Mini pelles 1T et 2,5T pour vos travaux de terrassement et d'aménagement.",
  "Location Bétonnière": "Bétonnières thermiques professionnelles pour préparer votre béton sur chantier.",
  "Location Plaque vibrante": "Compacteuses pour tasser et niveler vos sols et tranchées.",
  "Location tarière": "Foreuses thermiques avec mèches et rallonges au choix pour creuser facilement.",
  "Location Marteau piqueur, burineur": "Marteaux piqueurs électriques puissants pour démolition et perçage.",
  "Location Groupe électrogène": "Groupes électrogènes de 3200W à 8500W pour alimenter vos équipements partout.",
  "Location échafaudage": "Échafaudages modulables jusqu'à 6m pour travailler en hauteur en toute sécurité.",
  "Location Scie tous matériaux": "Scies professionnelles : à eau, onglet, circulaire, sabre et sauteuse.",
  "Location ponceuse": "Ponceuses girafe et excentrique avec aspirateur intégré optionnel.",
  "Location décolleuse": "Décolleuse à vapeur pour retirer facilement le papier peint.",
  "Location lève-plaque": "Lève-panneaux jusqu'à 3,1m pour poser vos plaques de plâtre seul.",
  "Location coupe carreaux carrelage": "Coupe-carreaux électrique pour des découpes précises et nettes.",
  "Location compresseur": "Compresseurs électriques pour alimenter vos outils pneumatiques.",
  "Location pistolet à peinture": "Pistolets à peinture avec ou sans compresseur pour une finition parfaite.",
  "Location déshumidifcateur": "Déshumidificateurs puissants pour assécher vos locaux après dégât des eaux.",
  "Location caméra thermique": "Caméras thermiques pour détecter les déperditions de chaleur et fuites.",
  "Location détecteurs": "Détecteurs de câbles et métaux pour localiser les conduites enterrées.",
  "Location aspirateur": "Aspirateurs industriels : plâtre, eau et poussière, cendres.",
  "Location outillages": "Outillage divers : poste à souder, pince à sertir, décapeur thermique...",
  "Location Broyeur de végétaux": "Broyeurs thermiques puissants pour réduire vos déchets verts en copeaux.",
  "Location fendeuse de buches": "Fendeuses thermiques pour préparer votre bois de chauffage facilement.",
  "Location débroussailleuse": "Débroussailleuses et coupe-bordures pour entretenir vos espaces verts.",
  "Location Motoculteur & motobineuse": "Motoculteurs et motobineuses pour préparer et aérer votre terre.",
  "Location scarificateur": "Scarificateurs thermiques pour aérer et revitaliser votre pelouse.",
  "Location rouleau à gazon et semoir": "Rouleaux, semoirs et accessoires pour créer une pelouse parfaite.",
  "Location souffleur": "Souffleurs dorsaux thermiques puissants pour nettoyer vos extérieurs.",
  "Location taille-haie": "Taille-haies thermiques de 50 à 60cm, avec ou sans perche télescopique.",
  "Location tronçonneuse & élagueuse": "Tronçonneuses et élagueuses pour couper et élaguer vos arbres en toute sécurité.",
  "Location Shampouineuse": "Injecteurs-extracteurs professionnels pour nettoyer moquettes et tissus.",
  "Location Nettoyeur ": "Nettoyeurs haute pression et lave-vitres pour un nettoyage impeccable."
};

const SubcategoriesPage = () => {
  const { slug } = useParams();
  const {seo, products} = useSeo();
  const navigate = useNavigate();
  
  // State pour les liens catalogue depuis l'admin
  const [catalogLinks, setCatalogLinks] = useState({
    url_3dloc: 'https://heyzine.com/flip-book/ada9b209bb.html',
    url_barnum: 'https://heyzine.com/flip-book/d8789c509d.html'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Récupérer les liens catalogue depuis l'API
  useEffect(() => {
    const fetchCatalogLinks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/catalog-links`);
        if (response.ok) {
          const data = await response.json();
          setCatalogLinks(current => ({
            url_3dloc: data.url_3dloc || current.url_3dloc,
            url_barnum: data.url_barnum || current.url_barnum
          }));
        }
      } catch (err) {
        console.log('Utilisation des liens catalogue par défaut');
      }
    };
    fetchCatalogLinks();
  }, [API_URL]);
  
  const categoryName = slugToCategory[slug];
  const univers = universInfo[categoryName];
  const subcategories = categoryStructure[categoryName] || [];

  // Obtenir le lien catalogue approprié pour cette catégorie
  const catalogType = categoryToCatalogType[categoryName];
  const catalogueUrl = catalogType === 'barnum' ? catalogLinks.url_barnum : catalogLinks.url_3dloc;
  
  // Compter les produits par sous-catégorie
  const productCounts = useMemo(() => {
    const counts = {};
    (products || []).forEach(product => {
      if (product.category === categoryName && product.isAvailable !== false) {
        counts[product.subcategory] = (counts[product.subcategory] || 0) + 1;
      }
    });
    return counts;
  }, [categoryName, products]);

  if (!categoryName || !univers) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Univers non trouvé</h1>
          <Link to="/" className="text-green-600 hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const IconComponent = univers.icon;

  return (
    <div className="min-h-screen" style={{ backgroundColor: `${univers.color}15` }}>
      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800 mb-6 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Retour</span>
          </button>

          {/* Header Content */}
          <div className="text-center mb-8">
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
              style={{ backgroundColor: univers.color }}
            >
              <IconComponent size={32} className="text-white" />
            </div>
            <h1 data-testid="universe-heading" className="text-3xl sm:text-4xl font-black text-slate-800 mb-4 break-words">
              {seo.h1}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {univers.description}
            </p>
            <PriorityLinks category={slug} testId="universe-priority-links" />
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href={catalogueUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-white text-slate-700 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
              style={{ borderColor: univers.color, borderWidth: '2px' }}
            >
              <BookOpen size={20} style={{ color: univers.color }} />
              Feuilleter le Catalogue complet
            </a>
            <a
              href="https://www.facebook.com/3dlocation"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
              style={{ backgroundColor: univers.color }}
            >
              <Facebook size={20} />
              Notre Facebook
            </a>
          </div>
        </div>
      </section>

      {/* Subcategories Grid */}
      <section className="pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {subcategories.map((subcategory, index) => {
              const imageUrl = subcategoryImages[subcategory] || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&auto=format';
              const description = subcategoryDescriptions[subcategory] || `Découvrez notre sélection de ${subcategory.toLowerCase()}.`;
              const productCount = productCounts[subcategory] || 0;
              
              // Construire le lien vers la page des produits (slug SEO-friendly)
              const subcategorySlug = slugify(subcategory);
              const link = `/equipements/${slug}/${subcategorySlug}`;

              return (
                <Link
                  key={index}
                  to={link}
                  data-testid={`subcategory-card-${index}`}
                  className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col sm:flex-row h-auto sm:h-48"
                >
                  {/* Image */}
                  <div className="sm:w-2/5 h-40 sm:h-full overflow-hidden flex-shrink-0 bg-slate-50 flex items-center justify-center">
                    <img
                      src={imageUrl}
                      alt={subcategory}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&auto=format';
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 flex flex-col justify-between min-h-0">
                    <div className="flex-1 min-h-0">
                      <h3 className="text-base font-bold text-slate-800 mb-1 group-hover:text-green-600 transition-colors line-clamp-2">
                        {subcategory}
                      </h3>
                      <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">
                        {description}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
                      <span className="text-xs text-slate-500">
                        {productCount} produit{productCount > 1 ? 's' : ''}
                      </span>
                      <span 
                        className="flex items-center gap-1 text-sm font-semibold transition-colors"
                        style={{ color: univers.color }}
                      >
                        Voir
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Voir tous les produits */}
          <div className="text-center mt-12">
            <Link
              to={`/equipements/${slug}`}
              data-testid="see-all-products-btn"
              className="inline-flex items-center gap-2 px-8 py-4 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              style={{ backgroundColor: univers.color }}
            >
              Voir tous les produits
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
      <UniversGuide slug={slug} />
    </div>
  );
};

export default SubcategoriesPage;
